'use client';

import OneSchemaImporter from '@oneschema/react';
import { FloatingPortal } from '@floating-ui/react';
import Link from 'next/link';
import { useState, useContext, type FC } from 'react';
import {
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  Flex,
  Select,
  Text,
} from '@/_components';
import { ClientsImportDIYDialogProps } from './ClientsImportDIYDialog.types';
import { importIndividualsFromClientList } from '@/_api/v2/customer-import-lists';
import { getIndividualImportTemplateLink } from '@/_api/admin-clients';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';

export const ClientsImportDIYDialog: FC<ClientsImportDIYDialogProps> = ({
  isOpen,
  onClose,
  onBack,
}) => {
  const [isOneSchemaOpen, setIsOneSchemaOpen] = useState(false);
  const [firmUserAssigneeId, setFirmUserAssigneeId] = useState<string>('');
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const { firmUsers } = useContext(FirmSettingsContext);

  const nonAssigneeItem = {
    name: 'Select Assignee (or leave blank if you want the "Assigned Staff Email" in client list to take precedence)',
    value: '',
  };

  const assigneeItems = [
    nonAssigneeItem,
    ...(firmUsers || []).map((u: any) => ({
      name: u.name,
      value: u.id,
    })),
  ];

  let previousStyle = '';

  // We have to hide the Hubspot icon because it blocks the submit button for OneSchema
  const hideHubspot = () => {
    const hubspotElById = document.getElementById(
      'hubspot-messages-iframe-container',
    );

    if (hubspotElById) {
      if (!previousStyle) {
        previousStyle = hubspotElById.getAttribute('style') || '';
      }

      hubspotElById.setAttribute(
        'style',
        `${previousStyle}; display: none !important;`.trim(),
      );
    }
  };

  const openHubspot = () => {
    const hubspotElById = document.getElementById(
      'hubspot-messages-iframe-container',
    );

    if (hubspotElById) {
      hubspotElById.setAttribute('style', previousStyle);
    }
  };

  const onLaunched = () => {
    hideHubspot();
  };

  const onSuccess = async (data: any) => {
    if (!token) return;

    const originalFileName = data?.sheet_metadata?.original_file_name;
    let records = data?.records || [];
    const errorRecords = data?.error_records || [];

    // errorRecords are records that had _warnings_ during import, not errors
    if (errorRecords.length > 0) {
      const extractedErrorRecords = errorRecords.map(
        (errorRecord: any) => errorRecord.data,
      );
      records = [...records, ...extractedErrorRecords];
    }

    try {
      await importIndividualsFromClientList({
        token,
        records: JSON.stringify(records),
        original_file_name: originalFileName,
        assigned_firm_user_id: firmUserAssigneeId,
      });

      addToast(
        'success',
        'Successfully queued the import of individuals. You will receive a notification when it is finished.',
      );
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      openHubspot();
    }
  };

  const onCancel = () => {
    openHubspot();
  };

  const onError = (error: any) => {
    console.error('error', error);
    openHubspot();
  };

  const onDownloadIndividualTemplate = async () => {
    try {
      const { document } = (await getIndividualImportTemplateLink({
        token,
      })) as any;

      globalThis.open(document?.data?.link, '_blank');
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Automatic Import (Individuals)"
      isNarrow
    >
      <Dialog.Content>
        <Flex gap={6} isColumn>
          <Flex gap={8} isBetween isYCentered padding={2}>
            <Alert type="info">
              <Flex isColumn={true} gap={4}>
                <Text type="small">
                  Instructions on how to import your client list can be found{' '}
                  <Link
                    href="https://helpcenter.soraban.com/how-to-import-clients-using-sorabans-automatic-import?hs_preview=NaEbIUfs-194146260292"
                    target="_blank"
                  >
                    here
                  </Link>{' '}
                  (opens in a new tab).
                </Text>
                <ButtonGroup>
                  <Button
                    onClick={onDownloadIndividualTemplate}
                    intent="secondary"
                    value="Download template"
                  />
                  <Link href="/import-lists" target="_blank">
                    <Button intent="secondary" value="Track import progress" />
                  </Link>
                </ButtonGroup>
              </Flex>
            </Alert>
          </Flex>

          <Alert type="info" icon="warning">
            The automatic import process works best when you <b>pre-format</b>{' '}
            your list. Please download the template above to see how to format
            it.
          </Alert>

          <Flex isColumn gap={8} isFullWidth>
            <Select
              helperText="Note: if this value is selected, it will override the 'Assigned Firm User Email' column in the import file."
              label="Assign to"
              placeholder="Select assignee"
              items={assigneeItems}
              selectedValue={firmUserAssigneeId}
              onChange={(e) => setFirmUserAssigneeId(e.target.value)}
              isFullWidth
            />

            <Button
              onClick={() => setIsOneSchemaOpen(true)}
              intent="primary"
              value="Upload individuals"
            />
          </Flex>
        </Flex>

        <FloatingPortal>
          <OneSchemaImporter
            isOpen={isOneSchemaOpen}
            onRequestClose={() => setIsOneSchemaOpen(false)}
            clientId="17d7b76c-4640-4834-b4f9-c9cf49bfb413"
            userJwt="eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiIxN2Q3Yjc2Yy00NjQwLTQ4MzQtYjRmOS1jOWNmNDliZmI0MTMiLCJ1c2VyX2lkIjoiPFVTRVJfSUQ-In0.D1FjRdc35Ps8KcdgzVzpnJebgj7pBL2B4eS48-kTtUg"
            templateKey="individuals_template_copy"
            importConfig={{ type: 'local' }}
            devMode={!globalThis?.location?.href?.includes('app.soraban.com')}
            className="oneschema-importer"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 1000,
            }}
            onLaunched={onLaunched}
            onSuccess={onSuccess}
            onCancel={onCancel}
            onError={onError}
          />
        </FloatingPortal>
      </Dialog.Content>
      <Dialog.Footer>
        <Button
          onClick={onBack}
          intent="ghost"
          value="Back"
          iconLeft="arrow-left"
        />
      </Dialog.Footer>
    </Dialog>
  );
};
